/**
 * Shortlist panel — "Top Picks for You".
 *
 * A clean left-docked panel that turns the three active preferences
 * (Lens × Priority × Budget) into a ranked top 3–10 list. Each card shows
 * name, a 1-line reason, a 1-line tradeoff, and (when School priority is
 * active) a grounded school line. Clicking a card flies the map to that
 * suburb and opens its verdict card.
 *
 * Phase 6 UX changes (from user feedback):
 *   - Removed the collapse button. Users said the panel "felt minimised /
 *     hidden". The panel is now persistently visible as the main shortlist.
 *   - Added a "Show next 5" button that pages from 5 → 10 picks (capped at
 *     `SHORTLIST_MAX_TOTAL`) so users can browse beyond the headline pick
 *     set without opening the map's full noise.
 *   - Added "See full map" — clears the shortlist (broadcasts an empty set)
 *     so every pin returns to full opacity. This is the explicit escape
 *     hatch for users who want to stop being narrowed.
 *   - When School priority is active, each card shows a grounded school
 *     line ("Strong secondary-school demand — prices reflect the zones").
 *
 * Design rules (unchanged):
 *   - No new complex UI. Plain DOM, one module, mirrors the floating-pill
 *     install pattern that every other control already uses.
 *   - Pure subscriber. The module observes `decision-lens`, `constraint-
 *     priority`, and `budget-band`; it never mutates them.
 *   - The panel broadcasts the current shortlist ID set so the map layer
 *     can fade non-shortlisted pins without importing this module's DOM.
 */

import type { Verdict } from "../data/verdicts";
import { getMode, subscribe as subscribeLens } from "./decision-lens";
import {
  getPriority,
  subscribePriority,
} from "./constraint-priority";
import {
  getBudgetBand,
  getBudgetBucket,
  getUserBudgetRange,
  subscribeBudgetBand,
  subscribeBudgetBucket,
  subscribeUserBudgetRange,
} from "./budget-band";
import {
  buildShortlist,
  buildStretchPicks,
  hasEnoughSignal,
  shortlistIds,
  type ShortlistItem,
  SHORTLIST_MIN,
} from "./auto-shortlist";
import { showVerdict } from "./verdict-card";
import { getPurchaseMode, subscribePurchaseMode } from "./purchase-mode";
import { getPropertyType, subscribePropertyType } from "./property-type";
import type { CityCode } from "./budget-format";
import {
  explainShortlist,
  deriveCityFromVerdict,
  type WhyLine,
} from "./why-this-beats-that";
import {
  getPublicSchoolRankRange,
  getPrivateSchoolRanks,
  SCHOOL_RANK_UNKNOWN_LINE,
} from "../data/school-ranks";
import { proximitySummary, proximityLine } from "./hyd-school-proximity";
import {
  getFullMapView,
  subscribeFullMapView,
} from "./full-map-view";
import {
  getCompareState,
  compareFromSurface,
  subscribeCompare,
} from "./compare-state";

/* -------------------------------------------------------------------------- */
/*  Broadcast (map layer subscribes to know which pins are shortlisted)       */
/* -------------------------------------------------------------------------- */

const shortlistListeners = new Set<(ids: Set<string>) => void>();
let currentIds: Set<string> = new Set();

/** Current shortlist ID set (for the map's non-shortlist fade expression). */
export function getShortlistIds(): Set<string> {
  return new Set(currentIds);
}

/** Subscribe to shortlist changes. Returns an unsubscribe function. */
export function subscribeShortlist(
  fn: (ids: Set<string>) => void
): () => void {
  shortlistListeners.add(fn);
  return () => {
    shortlistListeners.delete(fn);
  };
}

function broadcastShortlist(ids: Set<string>): void {
  currentIds = ids;
  for (const fn of Array.from(shortlistListeners)) {
    try {
      fn(new Set(ids));
    } catch {
      /* a broken subscriber must not break the rest */
    }
  }
}

/* -------------------------------------------------------------------------- */
/*  (Deprecated) Full-map toggle removed.                                     */
/*                                                                            */
/*  The "See full map" button now lives in the Decision-lens row as a reset.  */
/*  Instead of a temporary "hide picks but keep filters" toggle, the user     */
/*  clicks reset → `resetToExploration()` clears every filter → panel hides   */
/*  entirely (Exploration mode).                                              */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/*  Pagination caps                                                           */
/* -------------------------------------------------------------------------- */

/** Default visible picks. */
const SHORTLIST_PAGE = 5;
/** Hard ceiling — even with "Show next 5" twice, we cap to keep the UI legible. */
const SHORTLIST_MAX_TOTAL = 10;

/* -------------------------------------------------------------------------- */
/*  Panel install                                                             */
/* -------------------------------------------------------------------------- */

interface InstallArgs {
  dataset: readonly Verdict[];
  onPick: (v: Verdict) => void;
  /** Active city — needed so the "price note" under each comparator line
   *  uses the right currency + numeric range (A$ vs ₹). */
  city: CityCode;
}

const PANEL_ID = "hyperlocal-shortlist-panel";
const LIST_ID = "hyperlocal-shortlist-list";
const SUB_ID = "hyperlocal-shortlist-sub";
const ACTIONS_ID = "hyperlocal-shortlist-actions";
const WHY_ID = "hyperlocal-shortlist-why";

/* -------------------------------------------------------------------------- */
/*  Mobile stylesheet injection                                               */
/*                                                                            */
/*  On small viewports the left-docked 320px panel covers the map. Rather     */
/*  than dynamically rewriting inline styles on every resize, we inject a     */
/*  single <style> block that turns the panel into a bottom-sheet via a       */
/*  media query. ID selectors + `!important` keep the rules winning over the  */
/*  inline cssText that desktop still uses.                                   */
/* -------------------------------------------------------------------------- */
function ensureShortlistMobileStyles(): void {
  if (document.getElementById("hyperlocal-shortlist-mq")) return;
  const s = document.createElement("style");
  s.id = "hyperlocal-shortlist-mq";
  // On mobile the bottom-sheet version of this panel covered ~42vh of the
  // map — half the screen gone to chrome. User feedback was unambiguous:
  // "on mobile webpage don't show top picks for you, as it is covering the
  // whole map, give priority to the map visibility first". So on mobile we
  // HIDE this panel entirely. The picks still compute (the shortlist is
  // broadcast to the map so the top-match halos glow), but the list lives
  // in a dedicated full-screen "Picks" page reached from the top-right
  // chrome (see `installMobilePicksPage` in main-simple.ts) — same pattern
  // the filter page uses. The map stays a full-screen map.
  s.textContent = `
    @media (max-width: 720px), (max-height: 500px) {
      #${PANEL_ID} { display: none !important; }
    }
    /* When collapsed, hide every child of the panel EXCEPT the header row.
       The header itself carries data-sl-header so we can target children
       without knowing their ids. Using > child-combinator + :not() keeps
       this rule tight — no descendant leak. Padding is also trimmed so
       the collapsed pill is a compact bar, not a half-empty block. */
    #${PANEL_ID}[data-collapsed="true"] > :not([data-sl-header]) {
      display: none !important;
    }
    #${PANEL_ID}[data-collapsed="true"] {
      padding: 10px 14px !important;
    }
    #${PANEL_ID}[data-collapsed="true"] > [data-sl-header] {
      margin-bottom: 0 !important;
    }
  `;
  document.head.appendChild(s);
}

/** Install the shortlist panel + its subscriptions. Idempotent. */
export function installShortlistPanel(args: InstallArgs): void {
  if (document.getElementById(PANEL_ID)) return;
  ensureShortlistMobileStyles();

  const panel = document.createElement("aside");
  panel.id = PANEL_ID;
  panel.setAttribute("aria-label", "Top picks shortlist");
  // --- LEFT CONTROL-RAIL LAYOUT (priority-driven) ----------------------
  // The left edge is a vertical rail of pills. Under Priority = School
  // the rail adds the School-chart pill; otherwise only Compare sits
  // there (See-full-map is no longer a rail pill — it moved into the
  // Decision-lens row on the right as a reset button).
  //
  // Pills are 34px tall. Gaps between pills are 10px; gap between the
  // last pill and the Top Picks panel is 16px. This keeps the rail
  // reading as a single grouped column with no dead space.
  //
  //  Priority !== "school"         │   Priority === "school"
  //  ──────────────────────────────│──────────────────────────────
  //   y= 50..82   Compare 2 areas  │   y= 50..82   Compare 2 areas
  //                                │   y= 92..122  School chart
  //   y= 98       Top Picks panel  │   y=138       Top Picks panel
  //
  // The panel is also HIDDEN entirely when Lens = "none" (Exploration).
  // `applyPanelGeometry` is only called in Decision mode.
  //
  // `isolation:isolate` + explicit `pointer-events:auto` lock the panel's
  // stacking/hit-testing so nothing outside the panel rect can intercept
  // its clicks. Z-index 1300 keeps it above ambient controls (1200) and
  // map overlays; verdict/compare/school-chart modals (1500–2000) are
  // intentionally allowed to overlay when they're open.
  panel.style.cssText = `position:fixed;left:16px;z-index:1300;
    isolation:isolate;pointer-events:auto;
    width:320px;overflow:auto;
    background:rgba(22,22,22,0.94);backdrop-filter:blur(12px);
    color:#f1f1f1;border-radius:14px;padding:14px 14px 12px;
    border:1px solid rgba(255,214,128,0.18);
    box-shadow:0 12px 36px rgba(0,0,0,0.5);
    font:13px/1.45 system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;`;

  // Feature-detect dynamic viewport units (dvh). iOS Safari/Chrome measure
  // `vh` against the largest possible viewport (URL bar hidden), so when
  // the URL bar is showing, `100vh` overflows the visible area and the
  // bottom of the panel clips off-screen. `dvh` tracks the *current*
  // viewport and resolves the problem. Supported since iOS 15.4 / Chrome
  // 108 — we fall back to `vh` on older browsers.
  const supportsDvh =
    typeof CSS !== "undefined" &&
    typeof CSS.supports === "function" &&
    CSS.supports("max-height", "100dvh");
  const vUnit = supportsDvh ? "dvh" : "vh";

  function applyPanelGeometry(): void {
    if (getPriority() === "school") {
      // School-chart pill now occupies top 92..124 (was 134..166). Panel
      // sits 14px below that so the rail reads as a compact group.
      panel.style.top = "138px";
      panel.style.maxHeight = `calc(100${vUnit} - 178px)`;
    } else {
      panel.style.top = "98px";
      panel.style.maxHeight = `calc(100${vUnit} - 138px)`;
    }
  }
  applyPanelGeometry();

  // Header: title + collapse toggle. The toggle flips `data-collapsed` on
  // the panel; CSS (injected below) hides every child except the header
  // when collapsed. State is persisted to localStorage so the user's choice
  // survives page reloads.
  const header = document.createElement("div");
  header.setAttribute("data-sl-header", "1");
  header.style.cssText =
    "display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:2px;";
  const title = document.createElement("div");
  title.textContent = "Top Picks for You";
  title.style.cssText =
    "font-weight:700;font-size:14px;letter-spacing:0.01em;color:#FFE6B0;";

  const collapseBtn = document.createElement("button");
  collapseBtn.type = "button";
  collapseBtn.setAttribute("aria-controls", PANEL_ID);
  collapseBtn.style.cssText = `
    flex:0 0 auto;width:26px;height:26px;
    display:inline-flex;align-items:center;justify-content:center;
    background:rgba(255,214,128,0.10);
    color:#FFE6B0;
    border:1px solid rgba(255,214,128,0.28);
    border-radius:999px;
    font:700 16px/1 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
    cursor:pointer;padding:0;
    transition:background 140ms ease,transform 140ms ease;
  `;
  collapseBtn.addEventListener("mouseenter", () => {
    collapseBtn.style.background = "rgba(255,214,128,0.22)";
  });
  collapseBtn.addEventListener("mouseleave", () => {
    collapseBtn.style.background = "rgba(255,214,128,0.10)";
  });

  const COLLAPSE_KEY = "hyperlocal.topPicksCollapsed";
  function loadCollapsed(): boolean {
    // The collapse affordance is dead since picks moved INTO the Decide
    // page (its header — and the collapse toggle — is hidden there).
    // A stale "collapsed=1" from the old floating panel would hide every
    // pick inside the page with no visible way to expand, so the panel is
    // now always expanded regardless of the persisted flag.
    return false;
  }
  function saveCollapsed(v: boolean): void {
    try { localStorage.setItem(COLLAPSE_KEY, v ? "1" : "0"); } catch { /* ignore */ }
  }
  function applyCollapsed(v: boolean): void {
    panel.setAttribute("data-collapsed", v ? "true" : "false");
    // The minus/plus glyph is the affordance users immediately read as
    // "collapse / expand" — matches accordion convention across the web.
    collapseBtn.textContent = v ? "+" : "\u2212"; // minus sign (U+2212)
    collapseBtn.setAttribute("aria-expanded", v ? "false" : "true");
    collapseBtn.setAttribute(
      "aria-label",
      v ? "Expand top picks" : "Collapse top picks"
    );
    collapseBtn.title = v ? "Expand" : "Collapse";
    // When collapsed, the panel shrinks to fit the header only — release the
    // maxHeight constraint so it doesn't reserve empty vertical space.
    if (v) {
      panel.style.maxHeight = "";
      panel.style.overflow = "hidden";
    } else {
      applyPanelGeometry();
      panel.style.overflow = "auto";
    }
  }
  collapseBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    const next = panel.getAttribute("data-collapsed") !== "true";
    saveCollapsed(next);
    applyCollapsed(next);
  });

  header.appendChild(title);
  header.appendChild(collapseBtn);

  const sub = document.createElement("div");
  sub.id = SUB_ID;
  sub.style.cssText =
    "font-size:11px;color:#9A9A9A;margin-bottom:10px;letter-spacing:0.01em;";

  // "Why pick one over the other?" — compact comparator block. Sits between
  // the sub-header and the cards so the user reads the comparative claim
  // first, then drops into per-suburb detail. Hidden when there are < 2
  // visible picks (no peer to compare against).
  const why = document.createElement("div");
  why.id = WHY_ID;
  why.style.cssText = `
    display:none;flex-direction:column;gap:6px;
    margin-bottom:12px;padding:10px 11px;
    background:rgba(255,214,128,0.06);
    border:1px solid rgba(255,214,128,0.16);
    border-radius:10px;
  `;

  const list = document.createElement("div");
  list.id = LIST_ID;
  list.style.cssText = "display:flex;flex-direction:column;gap:8px;";

  // Action row — "Show next 5" + "See full map"
  const actions = document.createElement("div");
  actions.id = ACTIONS_ID;
  actions.style.cssText = `
    display:flex;gap:8px;justify-content:space-between;align-items:center;
    margin-top:10px;padding-top:10px;
    border-top:1px solid rgba(255,255,255,0.06);
  `;

  const moreBtn = document.createElement("button");
  moreBtn.type = "button";
  moreBtn.textContent = "Show next 5";
  moreBtn.style.cssText = `
    flex:1;background:rgba(255,214,128,0.12);color:#FFE6B0;
    border:1px solid rgba(255,214,128,0.28);
    padding:7px 10px;border-radius:8px;cursor:pointer;
    font:600 12px/1 system-ui;letter-spacing:0.02em;
    transition:background 140ms ease;
  `;
  moreBtn.addEventListener("mouseenter", () => {
    moreBtn.style.background = "rgba(255,214,128,0.22)";
  });
  moreBtn.addEventListener("mouseleave", () => {
    moreBtn.style.background = "rgba(255,214,128,0.12)";
  });

  // "See full map" and "School chart" both live in the top-left control
  // rail now (see `installFullMapPill` / `installSchoolChartPill` in
  // main-simple.ts). The panel registers its part of the See-full-map
  // action via `fullMapAction` below.

  actions.appendChild(moreBtn);

  panel.appendChild(header);
  panel.appendChild(sub);
  panel.appendChild(why);
  panel.appendChild(list);
  panel.appendChild(actions);

  document.body.appendChild(panel);

  // Restore collapsed state from localStorage on first paint so returning
  // users don't see a flash of the expanded panel before it snaps shut.
  applyCollapsed(loadCollapsed());

  // ----- Render pipeline -----
  let visibleCount = SHORTLIST_PAGE;
  let rafToken = 0;

  function scheduleRender(): void {
    if (rafToken) return;
    rafToken = requestAnimationFrame(() => {
      rafToken = 0;
      renderNow();
    });
  }

  function renderNow(): void {
    // The shortlist eligibility gate now runs on the exact `bucket` the
    // user selected (not the coarse 3-tier band). We forward bucket + city
    // + mode + type so `bucketFit` in auto-shortlist.ts can compare each
    // suburb's researched median against the bucket's numeric bounds.
    const ctx = {
      lens: getMode(),
      priority: getPriority(),
      band: getBudgetBand(),
      bucket: getBudgetBucket(),
      userRange: getUserBudgetRange(),
      city: args.city,
      purchaseMode: getPurchaseMode(),
      propertyType: getPropertyType(),
    };
    // Sub-header describes what's filtering.
    const parts: string[] = [];
    if (ctx.lens !== "none") parts.push(labelLens(ctx.lens));
    if (ctx.priority !== "none") parts.push(labelPriority(ctx.priority));
    if (ctx.band !== "none") parts.push(labelBand(ctx.band));
    sub.textContent = parts.length > 0 ? parts.join(" · ") : "Anyone";

    // Render list or empty state.
    list.innerHTML = "";
    why.innerHTML = "";
    why.style.display = "none";
    if (!hasEnoughSignal(ctx)) {
      // Exploration mode — no picks to show. The panel's only visible
      // home is the Decide page's "Your picks" section (CSS forces it
      // visible there), so render an honest empty state instead of
      // stale cards.
      broadcastShortlist(new Set());
      moreBtn.style.display = "none";
      const note = document.createElement("div");
      note.style.cssText =
        "color:#B8B8B8;font-size:12px;padding:8px 2px 2px;line-height:1.5;";
      note.textContent =
        "No picks yet — answer the questions above and matches appear here.";
      list.appendChild(note);
      return;
    }

    // "Full map" view inside Decide mode — filters stay put, but we
    // release the shortlist emphasis (pins unfade) and show a one-line
    // note instead of the picks list. One press of the 'Show picks'
    // toggle (in the picks section header) returns to the decision view.
    if (getFullMapView()) {
      broadcastShortlist(new Set());
      const note = document.createElement("div");
      note.style.cssText =
        "color:#B8B8B8;font-size:12px;padding:10px 2px 4px;line-height:1.5;";
      note.textContent =
        "Full map — press 'Show picks' to bring your shortlist back.";
      list.appendChild(note);
      moreBtn.style.display = "none";
      return;
    }

    const items = buildShortlist(args.dataset, ctx);
    if (items.length === 0) {
      const empty = document.createElement("div");
      empty.style.cssText =
        "color:#B8B8B8;font-size:12px;padding:8px 2px 2px;line-height:1.5;";
      empty.textContent =
        "Nothing clears the bar with these filters. Try loosening budget or priority.";
      list.appendChild(empty);
      broadcastShortlist(new Set());
      moreBtn.style.display = "none";
      return;
    }

    // Render header count line when fewer than 3 matches — users should
    // see "we found 2" rather than think the list is truncated.
    if (items.length < SHORTLIST_MIN) {
      const info = document.createElement("div");
      info.style.cssText =
        "color:#FFD37E;font-size:11px;margin-bottom:4px;letter-spacing:0.01em;";
      info.textContent = `Only ${items.length} suburb${items.length === 1 ? "" : "s"} clears the bar. Loosen budget or priority to broaden the list.`;
      list.appendChild(info);
    }

    const visible = items.slice(0, visibleCount);
    const showSchoolLine = ctx.priority === "school";

    // ---- Comparator block --------------------------------------------------
    // Build "why pick one over the other" only when we have ≥2 visible
    // suburbs. The comparator generator looks across the whole visible set
    // so each line picks a *different* distinguishing axis.
    if (visible.length >= 2) {
      const visibleVerdicts = visible
        .map((it) => args.dataset.find((v) => v.id === it.id))
        .filter((v): v is Verdict => Boolean(v));

      const lines = explainShortlist(visibleVerdicts, {
        priority: ctx.priority,
        budgetBand: ctx.band,
        city: args.city,
        purchaseMode: getPurchaseMode(),
        propertyType: getPropertyType(),
      });
      renderWhyBlock(why, lines);
    }

    for (const item of visible) {
      list.appendChild(renderCard(item, args, showSchoolLine));
    }

    // "Closest stretch options" — suburbs that rank well but sit exactly
    // one budget band above the user. Only shown when the user has set a
    // budget (otherwise the concept doesn't apply) and at least one
    // stretch candidate exists. Never mixed into Top Picks above.
    if (ctx.band !== "none") {
      const stretch = buildStretchPicks(args.dataset, ctx);
      if (stretch.length > 0) {
        list.appendChild(renderStretchDivider(stretch.length));
        for (const item of stretch) {
          list.appendChild(renderCard(item, args, showSchoolLine));
        }
      }
    }

    // Action buttons — show "Show next 5" only if there's a next page to
    // reveal, and we haven't hit the hard cap. "See full map" is a rail
    // pill now (lives outside this panel).
    const haveMore =
      items.length > visible.length && visibleCount < SHORTLIST_MAX_TOTAL;
    moreBtn.style.display = haveMore ? "inline-block" : "none";

    broadcastShortlist(shortlistIds(args.dataset, ctx));
  }

  // ----- Action wiring -----
  moreBtn.addEventListener("click", () => {
    visibleCount = Math.min(visibleCount + SHORTLIST_PAGE, SHORTLIST_MAX_TOTAL);
    scheduleRender();
  });
  // (Full-map toggle removed — reset is now a top-level action that
  // clears every filter via `resetToExploration`, which re-runs this
  // render path and hides the panel entirely in Exploration mode.)

  // ----- Subscribe to all five upstream stores -----
  // Reset pagination whenever the underlying signal set changes — bringing
  // a user back to the first page when they tweak filters keeps the list
  // honest about what's currently best.
  const onSignalChange = () => {
    visibleCount = SHORTLIST_PAGE;
    scheduleRender();
  };
  const offLens = subscribeLens(onSignalChange);
  const offPriority = subscribePriority(() => {
    // Priority change reflows the left rail (school pills appear/hide),
    // so recompute panel top + max-height before re-rendering the list.
    applyPanelGeometry();
    onSignalChange();
  });
  const offBand = subscribeBudgetBand(onSignalChange);
  // ALSO subscribe to bucket changes — switching between buckets within
  // the same band (e.g. A$600–700k → A$700–800k, both "tight") doesn't
  // fire `subscribeBudgetBand`, so without this hook the price badges
  // would stay painted at the stale bucket's midpoint.
  const offBucket = subscribeBudgetBucket(() => scheduleRender());
  // ALSO subscribe to userRange — the range-bar can nudge min/max inside
  // the same bucket (e.g. 600k → 650k while both handles stay inside the
  // 600–700k bucket). Without this, the ceiling-aware gradient wouldn't
  // recompute on those intra-bucket adjustments.
  const offRange = subscribeUserBudgetRange(() => scheduleRender());
  const offMode = subscribePurchaseMode(onSignalChange);
  const offType = subscribePropertyType(onSignalChange);
  // Compare state drives per-card button labels — re-render on transition
  // but DO NOT reset pagination (compare actions shouldn't lose the page).
  const offCompare = subscribeCompare(() => scheduleRender());
  // Full-map view toggle swaps the panel body between the picks list
  // and the "Full map" note. Re-render on every transition; keep
  // pagination intact so returning to picks preserves the page.
  const offFullMap = subscribeFullMapView(() => scheduleRender());

  // Cleanup handler — exposed in case future code wants to tear down.
  (panel as any).__hyperlocalCleanup = () => {
    offLens();
    offPriority();
    offBand();
    offBucket();
    offRange();
    offMode();
    offType();
    offCompare();
    offFullMap();
  };

  scheduleRender();
}

/* -------------------------------------------------------------------------- */
/*  Card renderer                                                             */
/* -------------------------------------------------------------------------- */

function renderCard(
  item: ShortlistItem,
  args: InstallArgs,
  showSchoolLine: boolean
): HTMLElement {
  const card = document.createElement("button");
  card.type = "button";
  card.setAttribute("aria-label", `View ${item.name}`);
  card.style.cssText = `text-align:left;border:0;cursor:pointer;padding:10px 12px;
    border-radius:10px;background:rgba(255,255,255,0.03);
    border-left:3px solid ${verdictColor(item.verdict)};
    color:#f1f1f1;font:inherit;display:flex;flex-direction:column;gap:4px;
    transition:background 140ms ease,transform 120ms ease;`;
  card.addEventListener("mouseenter", () => {
    card.style.background = "rgba(255,255,255,0.06)";
  });
  card.addEventListener("mouseleave", () => {
    card.style.background = "rgba(255,255,255,0.03)";
  });
  card.addEventListener("mousedown", () => {
    card.style.transform = "translateY(1px)";
  });
  card.addEventListener("mouseup", () => {
    card.style.transform = "translateY(0)";
  });

  const titleRow = document.createElement("div");
  titleRow.style.cssText =
    "display:flex;align-items:center;justify-content:space-between;gap:8px;";
  const name = document.createElement("span");
  name.textContent = item.name;
  name.style.cssText = "font-weight:600;font-size:13px;color:#FFE6B0;";

  const titleRight = document.createElement("span");
  titleRight.style.cssText = "display:inline-flex;align-items:center;gap:8px;flex:0 0 auto;";

  // Compare action — mirrors the compare-state mode so the user sees the
  // role this card plays at a glance ("Compare", "Pick as B", or "A ✓").
  // Uses role=button + keyboard handlers rather than a nested <button>
  // because nested interactive elements are invalid HTML.
  const cmp = document.createElement("span");
  const cs = getCompareState();
  const isA = cs.a?.id === item.id;
  const label =
    cs.mode === "waitB" && !isA ? "Pick as B"
    : cs.mode === "waitB" && isA ? "A ✓"
    : cs.mode === "waitA" ? "Set as A"
    : "Compare";
  const active = cs.mode !== "off" && !isA; // "Pick as B" / "Set as A"
  cmp.textContent = label;
  cmp.setAttribute("role", "button");
  cmp.setAttribute("tabindex", "0");
  cmp.setAttribute("aria-label", `${label} — ${item.name}`);
  cmp.style.cssText = `
    font-size:10.5px;letter-spacing:0.04em;text-transform:uppercase;
    padding:3px 8px;border-radius:999px;cursor:${isA ? "default" : "pointer"};
    font-weight:700;user-select:none;
    background:${active ? "rgba(255,211,126,0.22)" : "rgba(255,255,255,0.06)"};
    color:${isA ? "#9BE3A7" : active ? "#FFE6B0" : "#FFD37E"};
    border:1px solid ${isA ? "rgba(111,179,122,0.45)" : active ? "rgba(255,211,126,0.55)" : "rgba(255,255,255,0.16)"};
  `;
  if (!isA) {
    const activateCompare = (e: Event) => {
      e.stopPropagation();
      const v = args.dataset.find((x) => x.id === item.id);
      if (!v) return;
      compareFromSurface(v, args.dataset);
    };
    cmp.addEventListener("click", activateCompare);
    cmp.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") activateCompare(e);
    });
  }

  const dot = document.createElement("span");
  dot.setAttribute("aria-label", `Verdict ${item.verdict}`);
  dot.style.cssText = `width:8px;height:8px;border-radius:50%;display:inline-block;
    background:${verdictColor(item.verdict)};flex:0 0 auto;
    box-shadow:0 0 0 2px rgba(0,0,0,0.35);`;
  titleRight.appendChild(cmp);
  titleRight.appendChild(dot);
  titleRow.appendChild(name);
  titleRow.appendChild(titleRight);

  const reason = document.createElement("div");
  reason.textContent = item.reason;
  reason.style.cssText = "font-size:12px;color:#DCDCDC;line-height:1.4;";

  // The italic + dim styling already reads as "caveat / downside" — the
  // "Tradeoff:" prefix added a label users had to parse. A 60-year-old
  // reads "Limited school options" and instantly gets it; the word
  // "Tradeoff" was lexical noise. The sentence now carries itself.
  const tradeoff = document.createElement("div");
  tradeoff.textContent = item.tradeoff;
  tradeoff.style.cssText =
    "font-size:11.5px;color:#B8B8B8;line-height:1.4;font-style:italic;";

  card.appendChild(titleRow);
  card.appendChild(reason);
  card.appendChild(tradeoff);

  if (showSchoolLine) {
    // City-specific school framing under School priority:
    //   MEL → public-only rank range (zone-advantage model).
    //   HYD → proximity to top schools (no zoning language).
    const suburbVerdict = args.dataset.find((x) => x.id === item.id);
    const city = suburbVerdict ? deriveCityFromVerdict(suburbVerdict) : "mel";

    let text: string;
    let italic = false;

    if (city === "hyd") {
      const summary = proximitySummary(item.id, args.dataset);
      text = proximityLine(summary);
      italic = summary.tier === "unknown";
    } else {
      const publicRange = getPublicSchoolRankRange(item.id, 2);
      if (publicRange) {
        text =
          publicRange.count === 1 || publicRange.min === publicRange.max
            ? `Schools: Public Rank ${publicRange.min} (VIC)`
            : `Schools: Public Rank ${publicRange.min}–${publicRange.max} (VIC)`;
      } else if (getPrivateSchoolRanks(item.id, 1).length > 0) {
        text = "Private schools only — no ranked public school in this suburb";
        italic = true;
      } else {
        text = SCHOOL_RANK_UNKNOWN_LINE;
        italic = true;
      }
    }

    const sl = document.createElement("div");
    sl.textContent = text;
    sl.style.cssText =
      "font-size:11.5px;color:#9FCFE0;line-height:1.4;margin-top:2px;font-weight:600;";
    if (italic) sl.style.fontStyle = "italic";
    card.appendChild(sl);
  }

  card.addEventListener("click", () => {
    const v = args.dataset.find((x) => x.id === item.id);
    if (!v) return;
    // Compare intercept — when waiting for B and this card isn't A,
    // tapping the card body finalises compare instead of opening the
    // verdict. Same semantics as a map-pin tap in waitB.
    const cs2 = getCompareState();
    if (cs2.mode === "waitB" && cs2.a && cs2.a.id !== v.id) {
      compareFromSurface(v, args.dataset);
      return;
    }
    // When waiting for A, tapping the card body is the fastest way to
    // declare A (matches the pin-tap semantic in waitA).
    if (cs2.mode === "waitA") {
      compareFromSurface(v, args.dataset);
      return;
    }
    try {
      args.onPick(v);
    } catch {
      /* fall through to default */
    }
    showVerdict(v);
  });

  return card;
}

function verdictColor(v: Verdict["verdict"]): string {
  if (v === "green") return "#1d9e75";
  if (v === "amber") return "#d9a400";
  return "#c04040";
}

/**
 * Divider + heading for the "Closest stretch options" section. Visually
 * separates stretch picks from Top Picks so the user never mistakes an
 * out-of-budget suburb for an in-budget one.
 */
function renderStretchDivider(count: number): HTMLElement {
  const wrap = document.createElement("div");
  wrap.style.cssText = `
    display:flex;flex-direction:column;gap:2px;
    margin-top:10px;padding-top:10px;
    border-top:1px dashed rgba(255,214,128,0.28);
  `;
  const heading = document.createElement("div");
  heading.textContent = "Just above your budget";
  heading.style.cssText = `
    font-weight:700;font-size:11.5px;letter-spacing:0.05em;
    text-transform:uppercase;color:#FFD37E;
  `;
  const sub = document.createElement("div");
  sub.textContent = `${count} pick${count === 1 ? "" : "s"} cost a bit more than your budget`;
  sub.style.cssText = "font-size:11px;color:#9A9A9A;line-height:1.35;";
  wrap.appendChild(heading);
  wrap.appendChild(sub);
  return wrap;
}

/* -------------------------------------------------------------------------- */
/*  Comparator block renderer                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Render the comparator as a compact decision table: one row per suburb,
 * suburb name on top, two short positives and one short tradeoff below as
 * bullets. No paragraphs, no per-card extras (school/price notes live on
 * the detail card below, not here).
 *
 * Hides when fewer than 2 rows — the caller also guards, but the defence
 * stays local.
 */
function renderWhyBlock(container: HTMLElement, lines: WhyLine[]): void {
  container.innerHTML = "";
  if (lines.length < 2) {
    container.style.display = "none";
    return;
  }
  container.style.display = "flex";

  const heading = document.createElement("div");
  heading.textContent = "Why pick one over the other?";
  heading.style.cssText = `
    font-weight:700;font-size:11.5px;letter-spacing:0.05em;
    text-transform:uppercase;color:#FFD37E;margin-bottom:2px;
  `;
  container.appendChild(heading);

  for (const line of lines) {
    const row = document.createElement("div");
    row.style.cssText = "display:flex;flex-direction:column;gap:2px;";

    const name = document.createElement("div");
    name.textContent = line.name;
    name.style.cssText =
      "font-weight:700;font-size:12.5px;color:#FFE6B0;line-height:1.3;";
    row.appendChild(name);

    // Compact badge row — School tier + Price range. Scannable at a glance,
    // no body text. School colour-codes so Strong/Mixed/Weak is visible
    // without reading the word.
    const badges = document.createElement("div");
    badges.style.cssText =
      "display:flex;flex-wrap:wrap;gap:6px;margin:1px 0 2px;";
    badges.appendChild(makeBadge(line.schoolBadge, schoolBadgeTint(line.schoolBadgeTone)));
    badges.appendChild(makeBadge(line.priceBadge, { bg: "rgba(255,255,255,0.06)", fg: "#DCDCDC", border: "rgba(255,255,255,0.14)" }));
    row.appendChild(badges);

    // One-line price-context qualifier — anchors what the price usually buys
    // ("Typical 3-bed family house"). Dim + small so it reads as metadata,
    // not copy. Minimal height impact.
    const pctx = document.createElement("div");
    pctx.textContent = line.priceContext;
    pctx.style.cssText =
      "font-size:10.5px;color:#9A9A9A;line-height:1.2;letter-spacing:0.01em;";
    row.appendChild(pctx);

    const bullets = document.createElement("ul");
    bullets.style.cssText = `
      list-style:none;margin:0;padding:0 0 0 2px;
      display:flex;flex-direction:column;gap:1px;
    `;

    for (const pos of line.positives) {
      const li = document.createElement("li");
      li.textContent = `• ${pos}`;
      li.style.cssText = "font-size:12px;color:#E6E6E6;line-height:1.35;";
      bullets.appendChild(li);
    }

    const tli = document.createElement("li");
    // Bullet + italic dim colour already reads as caveat; "Tradeoff:" prefix
    // was extra cognitive load.
    tli.textContent = `• ${line.tradeoff}`;
    tli.style.cssText = "font-size:12px;color:#B8B8B8;line-height:1.35;font-style:italic;";
    bullets.appendChild(tli);

    row.appendChild(bullets);
    container.appendChild(row);
  }
}

interface BadgeTint {
  bg: string;
  fg: string;
  border: string;
}

function makeBadge(text: string, tint: BadgeTint): HTMLElement {
  const el = document.createElement("span");
  el.textContent = text;
  el.style.cssText = `
    display:inline-block;font-size:10.5px;line-height:1;
    padding:3px 7px;border-radius:999px;
    background:${tint.bg};color:${tint.fg};
    border:1px solid ${tint.border};
    font-weight:600;letter-spacing:0.02em;white-space:nowrap;
  `;
  return el;
}

function schoolBadgeTint(tone: "strong" | "mixed" | "weak" | "unknown"): BadgeTint {
  if (tone === "strong")
    return { bg: "rgba(111,179,122,0.16)", fg: "#9BE3A7", border: "rgba(111,179,122,0.38)" };
  if (tone === "weak")
    return { bg: "rgba(192,64,64,0.18)", fg: "#F1A0A0", border: "rgba(192,64,64,0.40)" };
  if (tone === "unknown")
    return { bg: "rgba(255,255,255,0.06)", fg: "#B8B8B8", border: "rgba(255,255,255,0.18)" };
  return { bg: "rgba(255,211,126,0.14)", fg: "#FFE6B0", border: "rgba(255,211,126,0.36)" };
}

function labelLens(m: ReturnType<typeof getMode>): string {
  return m === "family" ? "Family" : m === "bachelor" ? "Bachelor" : "Investor";
}
function labelPriority(p: ReturnType<typeof getPriority>): string {
  if (p === "school") return "School";
  if (p === "commute") return "Commute";
  if (p === "lifestyle") return "Lifestyle";
  return "Any priority";
}
function labelBand(b: ReturnType<typeof getBudgetBand>): string {
  if (b === "tight") return "Tight budget";
  if (b === "moderate") return "Mid-range budget";
  if (b === "stretch") return "Stretch budget";
  return "";
}
